import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import useSessionInitialization from '~/hooks/session/useSessionInitialization';
import { useResetMeasurements } from '~/hooks/useResetMeasurements';
import { useResetIntensityAnnotations } from '~/hooks/useResetIntensityAnnotations';
import { useConnectorJobOrchestrator } from '~/hooks/workflows/connector/useConnectorJobOrchestrator';
import {
    sessionDataAtom,
    sessionInitStatusAtom,
} from '~/state/atoms/sessionAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';

export default function SessionLayout() {
    const { sessionId } = useParams<{ sessionId: string }>();
    useConnectorJobOrchestrator();

    if (!sessionId) {
        return (
            <Typography color="error" p={2}>
                Missing session information.
            </Typography>
        );
    }

    return <SessionInitializer sessionId={sessionId} />;
}

function SessionInitializer({ sessionId }: { sessionId: string }) {
    const { error } = useSessionInitialization(sessionId);
    const sessionData = useRecoilValue(sessionDataAtom);
    const resetSessionData = useResetRecoilState(sessionDataAtom);
    const resetInitStatus = useResetRecoilState(sessionInitStatusAtom);
    const resetMeasurements = useResetMeasurements();
    const resetIntensityAnnotations = useResetIntensityAnnotations();
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const hasOpenedModal = useRef(false);

    // Clean up session state when unmounting (navigating away from session);
    // measurements and line profiles must not survive into the next session.
    useEffect(() => {
        return () => {
            resetSessionData();
            resetInitStatus();
            // Measurements, line profiles and circles hold values read from
            // this session's image; cleared here so they are never shown
            // over the next session's patient.
            resetMeasurements();
            resetIntensityAnnotations();
        };
    }, [
        resetSessionData,
        resetInitStatus,
        resetMeasurements,
        resetIntensityAnnotations,
    ]);

    // Auto-open Contour+ modal once session data is available
    useEffect(() => {
        if (sessionData && !hasOpenedModal.current) {
            hasOpenedModal.current = true;
            setWorkflowModal({
                open: true,
                page: WorkflowTypes.CONTOUR,
            });
        }
    }, [sessionData, setWorkflowModal]);

    // Gate only on metadata availability, not DICOM download
    if (error && !sessionData) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="100%"
            >
                <Typography color="error">
                    Failed to load session: {error.message}
                </Typography>
            </Box>
        );
    }

    if (!sessionData) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="100%"
                gap={2}
            >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    Loading session data...
                </Typography>
            </Box>
        );
    }

    return <Outlet />;
}
